import { proxyUrl } from './proxy-base.js'
/**
 * chain-eos/ipfs-gateways.ts — IPFS media, resolved through our own origin.
 *
 * History of what broke, in order:
 *
 * 1. A single hardcoded gateway (`ipfs.io`) degraded over time — slow, then
 *    down outright for some CIDs.
 * 2. Fanning each `<img>` out to N gateways in the browser fixed individual
 *    broken images, but every NFT grid now fires up to 6 concurrent requests
 *    PER THUMBNAIL. A screen with a dozen NFTs is 50-70 simultaneous requests
 *    to the same handful of public gateways — which is exactly the traffic
 *    shape their abuse protection is built to block. That is what turned
 *    "some images load slowly" into "ipfs.io 403s everything, in bulk,
 *    including images that worked seconds ago".
 * 3. Telegram's Mini App WebView additionally runs under COEP (cross-origin
 *    isolation), which blocks plain cross-origin `<img>` loads outright
 *    (`net::ERR_BLOCKED_BY_RESPONSE.NotSameOrigin`) unless the response is
 *    fetched in CORS mode or is same-origin to begin with.
 *
 * All three go away at once by never letting the browser talk to a public
 * gateway directly. Every IPFS asset resolves to `/ipfs-proxy/<cid...>` — our
 * own origin, always same-origin (no CORP/COEP problem, no crossOrigin dance).
 * The server behind that path (dev: Vite middleware, prod: a Vercel function)
 * tries the gateways ONCE, server-side, and caches the result — IPFS content
 * is addressed by its hash, so it is safe to cache forever. One browser
 * request per image, and after the first view of a given CID anywhere, every
 * later view is served straight from the edge cache, never touching a public
 * gateway again.
 */

/** Gateways the proxy tries, in order, server-side. Not used directly by the browser. */
export const IPFS_GATEWAYS = [
  'https://ipfs.io/ipfs/',
  'https://dweb.link/ipfs/',
  'https://w3s.link/ipfs/',
  'https://nftstorage.link/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
] as const

const GATEWAY_PATH_RE = /\/ipfs\/([^/?#]+(?:\/[^?#]*)?)/

/** Pulls the CID (and any sub-path) out of a gateway URL, or a raw `ipfs://`. */
export function ipfsCidFromUrl(url: string): string | null {
  if (url.startsWith('ipfs://')) return url.slice(7).replace(/^ipfs\//, '')
  const m = url.match(GATEWAY_PATH_RE)
  return m ? m[1] : null
}

/**
 * The one URL the browser should ever request for a piece of IPFS media: our
 * own same-origin proxy. Returns the original URL unchanged for anything that
 * isn't IPFS-shaped (a plain https image — most non-IPFS NFT media) — nothing
 * to proxy there.
 */
export function resolveMediaUrl(url: string): string {
  const cid = ipfsCidFromUrl(url)
  // proxyUrl(): same-origin on web, absolute Vercel URL in the native app
  // (its https://localhost origin has no /ipfs-proxy — see proxy-base.ts).
  return cid ? proxyUrl(`/ipfs-proxy/${cid}`) : url
}
